import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchPaymentMethods, deletePaymentMethod } from "@/api/payments";
import { PaymentMethod } from "@/types";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Edit, Trash, Search, ArrowUpDown, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

const PaymentMethodsList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State for search and sorting
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof PaymentMethod | "supportedCurrenciesCount";
    direction: "asc" | "desc";
  }>({
    key: "name",
    direction: "asc",
  });

  // State for delete confirmation
  const [deleteMethodItem, setDeleteMethodItem] =
    useState<PaymentMethod | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Fetch payment methods using React Query
  const { data: paymentMethods = [], isLoading } = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: fetchPaymentMethods,
  });

  // Handle sorting
  const handleSort = (
    column: keyof PaymentMethod | "supportedCurrenciesCount"
  ) => {
    setSortConfig((prev) => ({
      key: column,
      direction:
        prev.key === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Filter and sort payment methods
  const filteredAndSortedMethods = [...paymentMethods]
    .filter((method) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        method.name.toLowerCase().includes(query) ||
        (method.description && method.description.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => {
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      if (sortConfig.key === "supportedCurrenciesCount") {
        return (
          direction *
          (a.supportedCurrencies.length - b.supportedCurrencies.length)
        );
      }

      if (
        typeof a[sortConfig.key] === "string" &&
        typeof b[sortConfig.key] === "string"
      ) {
        return (
          direction *
          (a[sortConfig.key] as string).localeCompare(
            b[sortConfig.key] as string
          )
        );
      }

      if (
        typeof a[sortConfig.key] === "boolean" &&
        typeof b[sortConfig.key] === "boolean"
      ) {
        return (
          direction * (Number(a[sortConfig.key]) - Number(b[sortConfig.key]))
        );
      }

      if (
        typeof a[sortConfig.key] === "number" &&
        typeof b[sortConfig.key] === "number"
      ) {
        return (
          direction *
          ((a[sortConfig.key] as number) - (b[sortConfig.key] as number))
        );
      }

      return 0;
    });

  const addPaymentMethodButton = (
    <Button
      onClick={() => navigate("/admin/system/payment-methods/new")}
      className="whitespace-nowrap"
    >
      <Plus className="h-4 w-4 mr-2" />
      Add Payment Method
    </Button>
  );

  return (
    <AdminLayout title="Payment Methods" actionButton={addPaymentMethodButton}>
      <div className="flex justify-between items-center mb-6">
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Search and filter payment methods
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search payment methods..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50">
                <TableHead
                  className="w-[180px] cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    {sortConfig.key === "name" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortConfig.direction === "asc"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("platformFee")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Processing Fee</span>
                    {sortConfig.key === "platformFee" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortConfig.direction === "asc"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("supportedCurrenciesCount")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Supported Currencies</span>
                    {sortConfig.key === "supportedCurrenciesCount" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortConfig.direction === "asc"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedMethods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    {searchQuery
                      ? "No payment methods found matching your search"
                      : "No payment methods found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedMethods.map((method) => (
                  <TableRow key={method.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{method.name}</span>
                        {method.isDefault && (
                          <Badge
                            variant="outline"
                            className="bg-amber-500/10 text-amber-500 text-xs border-amber-200"
                          >
                            Default
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {method.description || "No description"}
                      </p>
                    </TableCell>
                    <TableCell>
                      {method.feeType === "percentage"
                        ? `${method.platformFee}%`
                        : `$${method.platformFee}`}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {method.supportedCurrencies.map((currency) => (
                          <Badge key={currency} variant="outline">
                            {currency}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={method.isActive ? "default" : "secondary"}
                        className={
                          method.isActive
                            ? "bg-green-500/10 text-green-500 hover:bg-green-500/20"
                            : "bg-gray-500/10 text-gray-500 hover:bg-gray-500/20"
                        }
                      >
                        {method.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            navigate(
                              `/admin/system/payment-methods/${method.id}`
                            )
                          }
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeleteMethodItem(method);
                            setConfirmDialogOpen(true);
                          }}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Payment Method</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the payment method{" "}
              <strong>{deleteMethodItem?.name}</strong>? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!deleteMethodItem) return;

                try {
                  await deletePaymentMethod(deleteMethodItem.id);
                  queryClient.invalidateQueries({
                    queryKey: ["paymentMethods"],
                  });
                  toast({
                    title: "Payment method deleted",
                    description: `${deleteMethodItem.name} has been deleted successfully.`,
                  });
                } catch (error) {
                  console.error("Error deleting payment method:", error);
                  toast({
                    title: "Error",
                    description:
                      error instanceof Error
                        ? error.message
                        : "An error occurred while deleting the payment method",
                    variant: "destructive",
                  });
                } finally {
                  setConfirmDialogOpen(false);
                  setDeleteMethodItem(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PaymentMethodsList;
 